import type { IUtils } from '@date-io/core/IUtils'
import { useCallback, useDebugValue, useEffect, useMemo, useRef, useState } from 'react'

import { getDisplayDate, validate } from '../../_helpers/text-field-helper'
import type { BasePickerProps } from '../../typings/BasePicker'
import type { MaterialUiPickersDate } from '../../typings/date'

import { useOpenState } from './useOpenState'
import { useUtils } from './useUtils'

export interface StateHookOptions {
  getDefaultFormat: () => string
}

const useValueToDate = (utils: IUtils<MaterialUiPickersDate>, { value, initialFocusedDate }: BasePickerProps) => {
  const nowRef = useRef(utils.date())
  const date = utils.date(value || initialFocusedDate || nowRef.current)

  return date && utils.isValid(date) ? date : nowRef.current
}

function useDateValues(props: BasePickerProps, options: StateHookOptions) {
  const utils = useUtils()
  const date = useValueToDate(utils, props)
  const format = props.format || options.getDefaultFormat()

  return { date, format }
}

export function usePickerState(props: BasePickerProps, options: StateHookOptions) {
  const { autoOk, disabled, readOnly, onAccept, onChange, onError, value } = props

  const utils = useUtils()
  const { isOpen, setIsOpen } = useOpenState(props)
  const { date, format } = useDateValues(props, options)
  const [pickerDate, setPickerDate] = useState(date)

  useEffect(() => {
    // if value was changed in closed state - treat it as accepted
    if (!isOpen && !utils.isEqual(pickerDate, date)) {
      setPickerDate(date)
    }
  }, [date, isOpen, pickerDate, utils])

  const acceptDate = useCallback(
    (acceptedDate: MaterialUiPickersDate) => {
      onChange(acceptedDate)
      if (onAccept) {
        onAccept(acceptedDate)
      }

      setIsOpen(false)
    },
    [onAccept, onChange, setIsOpen],
  )

  const wrapperProps = useMemo(
    () => ({
      format,
      open: isOpen,
      onClear: () => acceptDate(null),
      onAccept: () => acceptDate(pickerDate),
      onSetToday: () => setPickerDate(utils.date()),
      onDismiss: () => {
        setIsOpen(false)
      },
    }),
    [acceptDate, format, isOpen, pickerDate, setIsOpen, utils],
  )

  const pickerProps = useMemo(
    () => ({
      date: pickerDate,
      onChange: (newDate: MaterialUiPickersDate, isFinish = true) => {
        setPickerDate(newDate)

        if (isFinish && autoOk) {
          acceptDate(newDate)

          return
        }

        // simulate autoOk, but do not close the modal
        // if (variant === 'inline' || variant === 'static') {
        //   onChange(newDate)
        //   onAccept && onAccept(newDate)
        // }
      },
    }),
    [acceptDate, autoOk, pickerDate],
  )

  const validationError = validate(value, utils, props)
  useEffect(() => {
    if (onError) {
      onError(validationError, value)
    }
  }, [onError, validationError, value])

  const inputValue = getDisplayDate(date, format, utils, value === null, props)
  const inputProps = useMemo(
    () => ({
      inputValue,
      validationError,
      openPicker: () => !readOnly && !disabled && setIsOpen(true),
    }),
    [disabled, inputValue, readOnly, setIsOpen, validationError],
  )

  const pickerState = { pickerProps, inputProps, wrapperProps }

  useDebugValue(pickerState)

  return pickerState
}
